import { api, getAccessToken, isLoggedIn } from "./auth";
import type { WalletSnapshot } from "./types";

const DEFAULT_PACKS = [5, 10, 20, 50, 100];
const CLIENT = "chatforge";

function packsFromData(data: Record<string, unknown>): { amountUsd: number; label: string }[] {
  const notes = (data.notes || {}) as Record<string, unknown>;
  const raw = data.credit_packs_usd ?? data.creditPacksUsd ?? notes.credit_packs_usd ?? null;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((n) => ({ amountUsd: Number(n), label: `$${Number(n)}` }));
  }
  return DEFAULT_PACKS.map((usd) => ({ amountUsd: usd, label: `$${usd}` }));
}

function parseWalletUsd(data: Record<string, unknown>): number {
  const balances = (data.balances || {}) as Record<string, unknown>;
  for (const c of [data.wallet_usd, data.walletUsd, data.balanceUsd, balances.wallet_usd]) {
    if (c != null && c !== "" && Number.isFinite(Number(c))) return Number(c);
  }
  for (const c of [data.wallet_balance_cents, balances.wallet_balance_cents]) {
    if (c != null && Number.isFinite(Number(c))) return Number(c) / 100;
  }
  return 0;
}

function parseMember(data: Record<string, unknown>): boolean {
  if (data.subscription_active === true || data.member_active === true) return true;
  if (data.subscriptionActive === true) return true;
  const tier = String(data.tier || "").toLowerCase();
  return ["member", "starter", "pro", "business", "spark", "creator", "studio"].includes(tier);
}

export async function fetchWallet(): Promise<WalletSnapshot> {
  if (!isLoggedIn() || !getAccessToken()) {
    return { balanceUsd: 0, currency: "USD", packs: packsFromData({}) };
  }
  try {
    const caps = await api<Record<string, unknown>>("/api/auth/capabilities");
    const member = parseMember(caps);
    return {
      balanceUsd: parseWalletUsd(caps),
      currency: "USD",
      subscriptionActive: member,
      subscriptionLabel: member ? "Member" : null,
      stripeConfigured: true,
      packs: packsFromData(caps),
      adultOk: Boolean(caps.adult_ok ?? caps.adultOk),
    };
  } catch {
    return { balanceUsd: 0, currency: "USD", packs: packsFromData({}) };
  }
}

export async function startCheckout(amountUsd: number): Promise<string | null> {
  if (!getAccessToken()) throw new Error("Sign in to add suite credits.");
  const data = await api<Record<string, unknown>>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({
      tier: `credit_${Math.round(amountUsd)}`,
      amount_usd: amountUsd,
      client: CLIENT,
      success_url: `${window.location.origin}/?checkout=success`,
      cancel_url: `${window.location.origin}/?checkout=cancel`,
    }),
  });
  return (data.checkout_url as string) || (data.url as string) || null;
}

export async function subscribeMember(): Promise<string | null> {
  if (!getAccessToken()) throw new Error("Sign in to subscribe.");
  const data = await api<Record<string, unknown>>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({
      tier: "member",
      client: CLIENT,
      success_url: `${window.location.origin}/?checkout=success`,
      cancel_url: `${window.location.origin}/?checkout=cancel`,
    }),
  });
  return (data.checkout_url as string) || (data.url as string) || null;
}
